import toast from 'react-hot-toast';

// Global error handler setup
export function setupGlobalErrorHandlers() {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const message = event.reason?.message || '';

    // Silently ignore non-actionable errors (network failures, third-party)
    if (
      message.includes('Failed to fetch') ||
      message.includes('Load failed') ||
      message.includes('NetworkError') ||
      message.includes('Could not load')
    ) {
      event.preventDefault();
      return;
    }

    console.error('Unhandled promise rejection:', event.reason);

    // Show user-friendly error message
    if (message.includes('timeout')) {
      toast.error('Request timed out. Please check your connection and try again.');
    } else if (message.includes('network')) {
      toast.error('Network error. Please check your internet connection.');
    } else {
      toast.error('An unexpected error occurred. Please try again.');
    }

    // Prevent the default browser error handling
    event.preventDefault();
  });

  // Handle general errors
  window.addEventListener('error', (event) => {
    console.error('Global error:', event.error);
    
    // Don't show toast for script loading errors
    if (!event.filename.includes('.js')) {
      return;
    }
    
    toast.error('An unexpected error occurred. Please refresh the page.');
  });
}

// Utility to wrap async functions with error handling
export function withErrorHandling<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  options?: {
    errorMessage?: string;
    onError?: (error: Error) => void;
    showToast?: boolean;
  }
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      console.error('Error in wrapped function:', error);
      
      if (options?.onError) {
        options.onError(error as Error);
      }
      
      if (options?.showToast !== false) {
        toast.error(options?.errorMessage || 'An error occurred. Please try again.');
      }
      
      throw error;
    }
  }) as T;
}